import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Pencil, Check, X, User, Phone, Store, Lock, Eye, EyeOff, MapPin } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { confirmDialog } from "../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Customer-facing self-service account screen.
 * - Header card: avatar + name (top), phone pill (orange-on-white), market pill (green-on-white).
 * - Inline pencil-edits for full_name, phone, market_name → PUT /auth/me.
 * - Password change card → blocking confirm dialog → PUT /auth/me/password.
 * All endpoints accept any authenticated role so this screen also works for
 * drivers, mujahez, supervisors if linked from their headers in the future.
 */
const MyAccount = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const token = localStorage.getItem("jumlaty_token");
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  // Edit mode states (per-field)
  const [editing, setEditing] = useState(null); // "name" | "phone" | "market" | null
  const [draftName, setDraftName] = useState(user?.full_name || "");
  const [draftPhone, setDraftPhone] = useState(user?.phone || "");
  const [draftMarket, setDraftMarket] = useState(user?.market_name || "");
  const [saving, setSaving] = useState(false);

  // Password change
  const [showPwdForm, setShowPwdForm] = useState(false);
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  // Optional location (GPS pin) — moved out of signup on Feb 27, 2026.
  // Customer can capture it here anytime; the driver uses it for delivery.
  const [capturingLocation, setCapturingLocation] = useState(false);
  const captureLocation = () => {
    if (!navigator.geolocation) {
      toast.error("متصفحك لا يدعم تحديد الموقع");
      return;
    }
    setCapturingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await axios.put(
            `${API}/auth/me`,
            { lat: pos.coords.latitude, lng: pos.coords.longitude },
            authHeader,
          );
          await refreshUser?.();
          toast.success("تم حفظ موقعك بنجاح");
        } catch (e) {
          toast.error(e?.response?.data?.detail || "تعذر حفظ الموقع");
        } finally {
          setCapturingLocation(false);
        }
      },
      () => {
        toast.error("لم نتمكن من الحصول على موقعك. تأكّد من الإذن.");
        setCapturingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const startEdit = (field) => {
    setDraftName(user?.full_name || "");
    setDraftPhone(user?.phone || "");
    setDraftMarket(user?.market_name || "");
    setEditing(field);
  };

  const cancelEdit = () => setEditing(null);

  const saveField = async (field) => {
    const body =
      field === "name" ? { full_name: draftName.trim() } :
      field === "phone" ? { phone: draftPhone.trim() } :
      field === "market" ? { market_name: draftMarket.trim() } : {};
    setSaving(true);
    try {
      await axios.put(`${API}/auth/me`, body, authHeader);
      await refreshUser?.();
      setEditing(null);
      toast.success("تم حفظ التعديل بنجاح");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذر حفظ التعديل");
    } finally {
      setSaving(false);
    }
  };

  const submitPasswordChange = async () => {
    if (newPwd.length < 4) {
      toast.error("كلمة المرور الجديدة يجب أن تكون 4 أحرف على الأقل");
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error("كلمة المرور الجديدة وتأكيدها غير متطابقتين");
      return;
    }
    const ok = await confirmDialog("هل تريد تأكيد تغيير كلمة المرور؟", {
      force: true,
      tone: "danger",
      title: "تغيير كلمة المرور",
      confirmLabel: "تأكيد التغيير",
      cancelLabel: "إلغاء",
    });
    if (!ok) return;
    setSaving(true);
    try {
      await axios.put(`${API}/auth/me/password`,
        { current_password: currentPwd, new_password: newPwd },
        authHeader);
      toast.success("تم تغيير كلمة المرور بنجاح");
      setShowPwdForm(false);
      setCurrentPwd(""); setNewPwd(""); setConfirmPwd("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذر تغيير كلمة المرور");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F1E8] pb-16" dir="rtl">
      {/* Top bar */}
      <div className="bg-white border-b border-[#E8D9B5] sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            data-testid="back-btn"
            className="p-2 hover:bg-[#F4F1E8] rounded-full transition-colors"
          >
            <ArrowRight className="w-5 h-5 text-[#2D4A3E]" />
          </button>
          <h1 className="text-lg font-extrabold text-[#2D4A3E]">إدارة حسابي</h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 mt-6 space-y-6">
        {/* Header card — name + identity pills */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-md border border-[#E8D9B5] p-6"
        >
          <div className="flex flex-col items-center text-center gap-3">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#2D4A3E] to-[#4A6F36] flex items-center justify-center shadow-lg">
              <User className="w-10 h-10 text-white" strokeWidth={2.2} />
            </div>
            <h2 data-testid="profile-name" className="text-2xl font-extrabold text-[#2D4A3E]">
              {user?.full_name || "—"}
            </h2>
          </div>

          {/* Identity pills row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
            {/* Phone — orange on white */}
            <div className="flex items-center gap-3 px-4 py-3 bg-white rounded-2xl border-2 border-[#F5A623] shadow-sm">
              <div className="w-10 h-10 rounded-full bg-[#FFF4E0] flex items-center justify-center">
                <Phone className="w-5 h-5 text-[#F5A623]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] text-[#8B7E5A] font-medium">رقم الهاتف</div>
                <div data-testid="profile-phone" className="text-[#F5A623] font-bold tracking-wider truncate">
                  {user?.phone || "—"}
                </div>
              </div>
            </div>

            {/* Market — green on white */}
            <div className="flex items-center gap-3 px-4 py-3 bg-white rounded-2xl border-2 border-[#15803D] shadow-sm">
              <div className="w-10 h-10 rounded-full bg-[#DCFCE7] flex items-center justify-center">
                <Store className="w-5 h-5 text-[#15803D]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] text-[#5C6E64] font-medium">اسم الماركت</div>
                <div data-testid="profile-market" className="text-[#15803D] font-bold truncate">
                  {user?.market_name || "—"}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Editable fields card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.06 }}
          className="bg-white rounded-3xl shadow-md border border-[#E8D9B5] p-5 space-y-3"
        >
          <h3 className="text-base font-extrabold text-[#2D4A3E] mb-3">تعديل بياناتي</h3>

          <EditableRow
            label="الاسم الكامل"
            icon={<User className="w-4 h-4 text-[#2D4A3E]" />}
            value={user?.full_name}
            isEditing={editing === "name"}
            draft={draftName}
            onDraft={setDraftName}
            onStart={() => startEdit("name")}
            onCancel={cancelEdit}
            onSave={() => saveField("name")}
            saving={saving}
            testId="edit-name"
          />
          <EditableRow
            label="رقم الهاتف"
            icon={<Phone className="w-4 h-4 text-[#F5A623]" />}
            value={user?.phone}
            isEditing={editing === "phone"}
            draft={draftPhone}
            onDraft={setDraftPhone}
            onStart={() => startEdit("phone")}
            onCancel={cancelEdit}
            onSave={() => saveField("phone")}
            saving={saving}
            inputMode="tel"
            testId="edit-phone"
          />
          <EditableRow
            label="اسم الماركت"
            icon={<Store className="w-4 h-4 text-[#15803D]" />}
            value={user?.market_name}
            isEditing={editing === "market"}
            draft={draftMarket}
            onDraft={setDraftMarket}
            onStart={() => startEdit("market")}
            onCancel={cancelEdit}
            onSave={() => saveField("market")}
            saving={saving}
            testId="edit-market"
          />
        </motion.div>

        {/* Location (GPS) card — OPTIONAL. Requested Feb 27, 2026. */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.09 }}
          className={`rounded-3xl shadow-md border-2 p-5 ${
            user?.lat && user?.lng
              ? "bg-[#F0FDF4] border-[#15803D]"
              : "bg-[#FFF8E1] border-[#F5A623]"
          }`}
          data-testid="profile-location-card"
        >
          <div className="flex items-center gap-2 mb-2">
            <MapPin className={`w-5 h-5 ${user?.lat && user?.lng ? "text-[#15803D]" : "text-[#F5A623]"}`} />
            <h3 className="text-base font-extrabold text-[#2D4A3E]">
              موقعي على الخريطة
              <span className="text-[11px] font-medium text-[#8B7E5A] mr-2">(اختياري)</span>
            </h3>
          </div>

          {user?.lat && user?.lng ? (
            <p className="text-xs text-[#15803D] mb-3" data-testid="profile-coords-display">
              ✅ تم تحديد موقعك — سيصلك السائق مباشرة
              <span className="block mt-0.5 text-[#5C6E64]" dir="ltr">
                ({Number(user.lat).toFixed(5)}, {Number(user.lng).toFixed(5)})
              </span>
            </p>
          ) : (
            <p className="text-xs text-[#5C6E64] mb-3">
              حدّد موقعك الحالي على الخريطة ليتمكن السائق من الوصول إليك بسرعة. لا تحتاج تفعله فوراً — يمكنك إضافته لاحقاً.
            </p>
          )}

          <button
            type="button"
            onClick={captureLocation}
            disabled={capturingLocation}
            data-testid="profile-capture-location"
            className={`w-full font-bold py-2.5 rounded-full text-sm disabled:opacity-50 transition-colors ${
              user?.lat && user?.lng
                ? "bg-[#15803D] hover:bg-[#126B32] text-white"
                : "bg-[#3B82F6] hover:bg-[#2563EB] text-white"
            }`}
          >
            {capturingLocation
              ? "جاري التحديد..."
              : user?.lat && user?.lng
                ? "🔄 إعادة تحديد الموقع"
                : "📍 حدّد موقعي الحالي"}
          </button>
        </motion.div>

        {/* Password change card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="bg-white rounded-3xl shadow-md border border-[#E8D9B5] p-5"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-[#E85D45]" />
              <h3 className="text-base font-extrabold text-[#2D4A3E]">كلمة المرور</h3>
            </div>
            {!showPwdForm && (
              <button
                onClick={() => setShowPwdForm(true)}
                data-testid="open-pwd-form"
                className="w-9 h-9 rounded-full bg-gradient-to-br from-[#2D4A3E] to-[#4A6F36] hover:opacity-90 text-white flex items-center justify-center shadow-md transition-all"
                title="تعديل كلمة المرور"
              >
                <Pencil className="w-4 h-4" />
              </button>
            )}
          </div>

          {!showPwdForm && (
            <p className="text-sm text-[#5C6E64]">اضغط القلم لتغيير كلمة المرور.</p>
          )}

          {showPwdForm && (
            <div className="space-y-3 mt-3">
              <PasswordField
                label="كلمة المرور الحالية"
                value={currentPwd}
                onChange={setCurrentPwd}
                show={showCurrent}
                onToggle={() => setShowCurrent((s) => !s)}
                testId="current-pwd"
              />
              <PasswordField
                label="كلمة المرور الجديدة"
                value={newPwd}
                onChange={setNewPwd}
                show={showNew}
                onToggle={() => setShowNew((s) => !s)}
                testId="new-pwd"
              />
              <PasswordField
                label="تأكيد كلمة المرور الجديدة"
                value={confirmPwd}
                onChange={setConfirmPwd}
                show={showNew}
                onToggle={() => setShowNew((s) => !s)}
                testId="confirm-pwd"
              />
              <div className="flex gap-2 pt-2">
                <button
                  onClick={submitPasswordChange}
                  disabled={saving || !currentPwd || !newPwd || !confirmPwd}
                  data-testid="submit-pwd-change"
                  className="flex-1 bg-[#15803D] hover:bg-[#126B32] disabled:opacity-50 text-white font-bold py-2.5 rounded-2xl transition-colors"
                >
                  {saving ? "جاري الحفظ..." : "حفظ التغيير"}
                </button>
                <button
                  onClick={() => { setShowPwdForm(false); setCurrentPwd(""); setNewPwd(""); setConfirmPwd(""); }}
                  data-testid="cancel-pwd-change"
                  className="px-5 bg-[#F4F1E8] hover:bg-[#E8D9B5] text-[#2D4A3E] font-bold py-2.5 rounded-2xl transition-colors"
                >
                  إلغاء
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

// ─── Sub-components ────────────────────────────────────────────────────────

const EditableRow = ({ label, icon, value, isEditing, draft, onDraft, onStart, onCancel, onSave, saving, inputMode, testId }) => (
  <div className="flex items-center gap-3 p-3 bg-[#F4F1E8] rounded-2xl">
    <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-sm shrink-0">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-[11px] text-[#8B7E5A] font-medium">{label}</div>
      {!isEditing ? (
        <div className="text-[#2D4A3E] font-bold truncate">{value || "—"}</div>
      ) : (
        <input
          autoFocus
          value={draft}
          inputMode={inputMode}
          onChange={(e) => onDraft(e.target.value)}
          data-testid={`${testId}-input`}
          className="w-full bg-white border border-[#E8D9B5] rounded-lg px-2 py-1 text-[#2D4A3E] font-bold focus:outline-none focus:border-[#15803D]"
        />
      )}
    </div>
    {!isEditing ? (
      <button
        onClick={onStart}
        data-testid={`${testId}-btn`}
        className="w-9 h-9 rounded-full bg-gradient-to-br from-[#2D4A3E] to-[#4A6F36] hover:opacity-90 text-white flex items-center justify-center shadow-md transition-all shrink-0"
        title={`تعديل ${label}`}
      >
        <Pencil className="w-4 h-4" />
      </button>
    ) : (
      <div className="flex gap-1 shrink-0">
        <button
          onClick={onSave}
          disabled={saving}
          data-testid={`${testId}-save`}
          className="w-9 h-9 rounded-full bg-[#15803D] hover:bg-[#126B32] disabled:opacity-50 text-white flex items-center justify-center shadow-md transition-colors"
          title="حفظ"
        >
          <Check className="w-4 h-4" />
        </button>
        <button
          onClick={onCancel}
          disabled={saving}
          data-testid={`${testId}-cancel`}
          className="w-9 h-9 rounded-full bg-[#E85D45] hover:bg-[#C24A35] disabled:opacity-50 text-white flex items-center justify-center shadow-md transition-colors"
          title="إلغاء"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    )}
  </div>
);

const PasswordField = ({ label, value, onChange, show, onToggle, testId }) => (
  <div>
    <label className="block text-[11px] text-[#8B7E5A] font-medium mb-1">{label}</label>
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full bg-[#F4F1E8] border border-[#E8D9B5] rounded-2xl px-3 py-2.5 text-[#2D4A3E] font-bold focus:outline-none focus:border-[#15803D] pl-10"
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-white flex items-center justify-center transition-colors"
        tabIndex={-1}
      >
        {show ? <EyeOff className="w-4 h-4 text-[#5C6E64]" /> : <Eye className="w-4 h-4 text-[#5C6E64]" />}
      </button>
    </div>
  </div>
);

export default MyAccount;
